/**
 * page-layers.tsx — where a layer drawn over the page renders, and which of
 * them go inert under a dialog.
 *
 * While a dialog is open the app's root is `inert`: nothing in it takes a
 * click, a key or a screen reader. A layer left inside the root would still
 * draw over the dialog on its higher rung of the z-ladder and be dead — its
 * close button unclickable, its message never announced. So every layer that
 * must stay live while a dialog is up renders into the body beside the
 * dialogs, through `abovePage`: the dialogs themselves (Modal), the toasts,
 * the page top's strip of banners above the dialogs, the window that stays
 * live over every dialog and moment (the moderator's call), what hangs from a
 * control in a dialog or on one of those (a popover, a hover card, a tip
 * beside its control), a tooltip at the pointer, and a line that must be heard
 * over a dialog (a StatusLine overDialogs). `layer-above-page-in-root` holds
 * the sheets on the rungs over the moments to it.
 *
 * Each names the rung it is drawn on as it goes above the page, and what
 * renders in it is told (HighRung): what hangs from a control there — a
 * popover, a hover card, a tip — hangs on that rung, over it and live
 * (useHang). A dialog names none, its own layer telling what hangs in it
 * where it is (ModalLayer), and so does a line only a screen reader hears.
 * The toasts' and the page top's strip once said their rung beside the call,
 * by hand, and the rest said nothing: what hung from them would have fallen
 * to the page, under its own layer and dead under any dialog.
 *
 * A floating window renders into the body too, so no container clips it, but
 * it belongs to the page: `withPage` marks it as the page's, and it goes inert
 * with the root under a dialog, the dialog's scrim dimming it — from the
 * start, when it opens under one. So does what hangs from a control on the
 * page, a popover, a hover card or a tip beside its control: useHang
 * (modal-layers.ts) hands it `withPage`, and `abovePage` only to one that
 * hangs from a dialog or from a layer that stays live over the dialogs.
 *
 * What React draws into an element of the page that a script made and moves
 * itself — a city's marker on the globe, its signpost carried with it —
 * renders into that element, `intoElement`: it is the page's, inert with the
 * root, being inside it.
 *
 * The open modals are counted in one place, their layers (modal-layers.ts),
 * which say when the page is inert: as the first opens, and live again as the
 * last closes (`setPageInert`). Set at once, not on a render: focus going
 * back into a window as a dialog closes needs the window live already.
 *
 *   return abovePage(<div className={styles.stack}>{children}</div>, 'toast');
 *   return withPage(panel);
 */

import { createContext } from 'react';
import type { ReactNode, ReactPortal } from 'react';
import { createPortal } from 'react-dom';

/**
 * A rung over the dialogs a layer above the page is drawn on: a moment's, the
 * window that stays live over every dialog and moment, the toasts', a
 * notice's, a tooltip's at the pointer.
 */
export type Rung = 'moment' | 'live' | 'toast' | 'notice' | 'tooltip';

/**
 * Each rung as tokens.css names it: the rungs it declares over the moments,
 * every one (above-page-rung-unlike-its-sheet holds the two to each other,
 * and a layer's sheet to the rung it names).
 */
export const RUNG_Z: Record<Rung, string> = {
  moment: 'var(--z-moment)',
  live: 'var(--z-live-window)',
  toast: 'var(--z-toast)',
  notice: 'var(--z-notification)',
  tooltip: 'var(--z-tooltip)',
};

/**
 * The rung over the dialogs that what renders here is drawn on, or null — the
 * page, a dialog, or a line no one sees. abovePage provides the rung it is
 * handed; what hangs from a control here hangs on it (modal-layers.ts,
 * useHang), where its own --z-window would put it under the moment, the toast
 * or the notice that opened it.
 */
export const HighRung = createContext<Rung | null>(null);

/** What marks an element in the body as the page's: inert with the root under a dialog. */
const PAGE_LAYER = 'data-page-layer';

/** Whether the page is inert now: a modal is open over it. */
let pageInert = false;

/**
 * Rendered into the body beside the dialogs, live while one is open, on the
 * rung its sheet draws it on — named here, so what hangs from it hangs on it
 * (HighRung); null for a dialog, whose ModalLayer places what hangs in it,
 * and for a layer nothing can hang from, a line only a screen reader hears.
 */
export function abovePage(children: ReactNode, rung: Rung | null): ReactPortal {
  return createPortal(<HighRung.Provider value={rung}>{children}</HighRung.Provider>, document.body);
}

/**
 * Rendered into the body as the page's: inert with the root while a dialog is
 * open. Each render reads the page as it stands, and the page's own changes
 * reach it at once (setPageInert), so the two never disagree.
 */
export function withPage(children: ReactNode): ReactPortal {
  return createPortal(
    <div {...{ [PAGE_LAYER]: '' }} inert={pageInert}>
      {children}
    </div>,
    document.body,
  );
}

/**
 * Rendered into an element of the page that React did not make, which a
 * script draws and moves: what is drawn moves and hides with it. The
 * element's own content is the script's; this adds to it.
 */
export function intoElement(children: ReactNode, element: Element): ReactPortal {
  return createPortal(children, element);
}

/**
 * The page, and every layer rendered with it, inert or live: inert while a
 * modal is open over it. The modals' layers (modal-layers.ts) set it as the
 * first opens and the last closes, and nothing else does.
 */
export function setPageInert(inert: boolean): void {
  if (inert === pageInert) return;
  pageInert = inert;
  const page = [document.getElementById('root'), ...document.querySelectorAll(`[${PAGE_LAYER}]`)];
  for (const element of page) {
    if (inert) element?.setAttribute('inert', '');
    else element?.removeAttribute('inert');
  }
}
